import { useState, type ReactNode, type MouseEvent } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { WAF_YELLOW } from '../theme';
import { useColorMode } from '../colorMode';
import { ColorModeToggle } from './ColorModeToggle';
import { useAuth } from '../contexts/AuthContext';
import type { Role } from '../lib/auth/types';
import { roleDisplayName } from '../lib/roles';
import wafLogoUrl from '../assets/waf-logo.png';
// Dark-background variant of the logo. File may not exist yet — Vite's
// import.meta.glob lets us treat it as optional without breaking the build.
const darkLogoModules = import.meta.glob<{ default: string }>('../assets/waf-logo-dark.png', { eager: true });
const wafLogoDarkUrl: string | null =
  Object.values(darkLogoModules)[0]?.default ?? null;

// Primary tabs — the day-to-day pages, shown inline for anyone with the role.
// `roles`: Admin (super admin) + User (regular staff admin) share these;
// Entrant gets its own two.
const PRIMARY_TABS: Array<{ label: string; to: string; match: string[]; roles: Role[] }> = [
  { label: 'Dashboard', to: '/dashboard', match: ['/dashboard'], roles: ['Admin', 'User'] },
  // PR (read-only agency) sees Entries only — no dashboard, change requests or admin tools.
  { label: 'Entries', to: '/entries', match: ['/entries', '/entry'], roles: ['Admin', 'User', 'PR'] },
  { label: 'Change requests', to: '/change-requests', match: ['/change-requests'], roles: ['Admin', 'User'] },
  { label: 'My entries', to: '/entrant', match: ['/entrant', '/submit-change'], roles: ['Entrant'] },
];

// Tools collapsed into the "Admin tools" dropdown so the primary tabs stay
// uncluttered. Each tab declares which roles may see it: super-admins ('Admin')
// see everything; EMAP staff ('User') see the granted subset. Grouped into
// labelled sections so the workflow reads clearly: data in -> email -> audit.
type AdminTab = { label: string; to: string; match: string[]; roles: Role[] };
const ADMIN_GROUPS: Array<{ heading: string; tabs: AdminTab[] }> = [
  {
    heading: 'Insights',
    tabs: [
      { label: 'Metrics', to: '/metrics', match: ['/metrics'], roles: ['Admin'] },
    ],
  },
  {
    heading: 'Administration',
    tabs: [
      { label: 'Import from Evessio', to: '/import', match: ['/import'], roles: ['Admin', 'User'] },
      { label: 'Onboarding entrants', to: '/onboarding', match: ['/onboarding'], roles: ['Admin'] },
      { label: 'Review onboarding', to: '/review-onboarding', match: ['/review-onboarding'], roles: ['Admin', 'User'] },
      { label: 'Users', to: '/users', match: ['/users'], roles: ['Admin', 'User'] },
      { label: 'Export to Excel', to: '/export-excel', match: ['/export-excel'], roles: ['Admin', 'User'] },
      { label: 'Download images', to: '/image-export', match: ['/image-export'], roles: ['Admin', 'User', 'PR'] },
      { label: 'Export to WBD', to: '/export-wbd', match: ['/export-wbd'], roles: ['Admin'] },
      { label: 'Export changed to WBD', to: '/export-wbd-changed', match: ['/export-wbd-changed'], roles: ['Admin'] },
    ],
  },
  {
    heading: 'Email',
    tabs: [
      { label: 'Email templates', to: '/email-templates', match: ['/email-templates'], roles: ['Admin', 'User'] },
      { label: 'Email log', to: '/email-log', match: ['/email-log'], roles: ['Admin', 'User'] },
    ],
  },
  {
    heading: 'Security & audit',
    tabs: [
      { label: 'Failed logins', to: '/failed-logins', match: ['/failed-logins'], roles: ['Admin'] },
      { label: 'Audit log', to: '/audit', match: ['/audit'], roles: ['Admin'] },
    ],
  },
];

export function Layout({ children }: { children: ReactNode }) {
  const { user, hasRole, logout, viewAsRole, setViewAsRole, availableViewAsRoles, isSimulating } = useAuth();
  const displayName = user?.name || user?.email || 'Account';
  const initial = displayName.charAt(0).toUpperCase();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Tabs a user can see are driven by effective role (real role, or the
  // one they're simulating via the "View as" picker). hasRole reflects
  // the simulation when active — see AuthContext.
  const visibleTabs = PRIMARY_TABS.filter((t) => t.roles.some((r) => hasRole(r)));
  const activeTab = visibleTabs.findIndex((t) => t.match.some((p) => pathname.startsWith(p)));
  // Admin-tools dropdown is role-filtered: super-admins (Admin) get everything,
  // EMAP staff (User) get the subset their role is granted. Respects view-as
  // simulation via hasRole.
  const visibleAdminGroups = ADMIN_GROUPS
    .map((g) => ({ ...g, tabs: g.tabs.filter((t) => t.roles.some((r) => hasRole(r))) }))
    .filter((g) => g.tabs.length > 0);
  const adminTabs = visibleAdminGroups.flatMap((g) => g.tabs);
  const onAdminPage = adminTabs.some((t) => t.match.some((p) => pathname.startsWith(p)));
  const { mode } = useColorMode();
  const logoSrc = mode === 'dark' && wafLogoDarkUrl ? wafLogoDarkUrl : wafLogoUrl;
  // Phone-sized viewports collapse the account-menu text block to just the
  // avatar and switch the primary tabs to a scrollable strip.
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);
  const openMenu = (e: MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);
  const [adminAnchor, setAdminAnchor] = useState<HTMLElement | null>(null);
  const adminOpen = Boolean(adminAnchor);
  const goTo = (path: string) => {
    closeMenu();
    navigate(path);
  };

  // The user's "primary" role — highest real role they hold. Drives the
  // "your role" subtitle in the view-as menu, and the active-tick state
  // when no simulation is running.
  const primaryRole = user
    ? (['Admin', 'User', 'PR', 'Entrant'] as Role[]).find((r) => user.roles.includes(r)) ?? null
    : null;

  // What role is currently "in effect" for UI gating — simulated or primary.
  const effectiveRole = viewAsRole ?? primaryRole;

  const pickViewAs = (role: Role) => {
    closeMenu();
    // Clicking the user's primary role stops any ongoing simulation
    // rather than setting viewAsRole to match — cleaner than "simulating
    // your own role."
    setViewAsRole(role === primaryRole ? null : role);
    // Land on the right home for the newly-picked role so the simulated
    // nav immediately matches a valid page.
    navigate(role === 'Entrant' ? '/entrant' : '/');
  };

  // Most pages read better capped at a max width; only pages with wide data
  // grids (currently Onboarding) go full-width so their columns aren't clipped.
  // Add routes here to opt them in.
  const isWideContent = pathname.startsWith('/onboarding');
  // Entry-detail views (staff /entry/:id, entrant /submit-change) get a wider —
  // but not full-bleed — container so the two-column metadata layout has room to
  // breathe (~70% of the page, capped for readability on very wide screens).
  const isEntryDetail = pathname.startsWith('/entry/') || pathname.startsWith('/submit-change');

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', overflowX: 'clip' }}>
      <AppBar position="sticky">
        <Toolbar
          sx={{
            minHeight: { xs: 60, sm: 80 },
            py: { xs: 0.5, sm: 1 },
            px: { xs: 1.5, sm: 4 },
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
          }}
        >
          {/* Left — colour mode toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ColorModeToggle />
          </Box>

          {/* Centered brand — clicking routes to the current role's home. */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <ButtonBase
              component={RouterLink}
              to="/"
              aria-label="Go to home"
              sx={(t) => ({
                borderRadius: 1,
                '&:focus-visible': { boxShadow: `0 0 0 2px ${t.palette.primary.main}` },
              })}
            >
              <Box
                component="img"
                src={logoSrc}
                alt="World Architecture Festival — 12-14 November 2025. Miami Beach Convention Center"
                sx={{ height: { xs: 32, sm: 56 }, width: 'auto', maxWidth: '100%', display: 'block' }}
              />
            </ButtonBase>
          </Box>

          {/* Right — actor menu trigger */}
          <Stack direction="row" justifyContent="flex-end">
            <ButtonBase
              onClick={openMenu}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="Open account menu"
              sx={(t) => ({
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0, sm: 1.5 },
                px: { xs: 0.5, sm: 1 },
                py: 0.5,
                borderRadius: 2,
                transition: 'background-color 120ms ease',
                '&:hover': {
                  backgroundColor:
                    t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                },
                '&:focus-visible': {
                  boxShadow: `0 0 0 2px ${t.palette.primary.main}`,
                },
              })}
            >
              {/* Name + email — hidden on phones to leave room for the avatar. */}
              <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                  {displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {user?.email ?? ''}
                </Typography>
              </Box>
              <Avatar
                sx={(t) => ({
                  width: 32,
                  height: 32,
                  bgcolor: t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'grey.200',
                  color: 'text.primary',
                })}
              >
                {initial}
              </Avatar>
            </ButtonBase>
            <Menu
              anchorEl={menuAnchor}
              open={menuOpen}
              onClose={closeMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { minWidth: 200, mt: 1 } } }}
            >
              <MenuItem onClick={() => goTo('/account')}>Account settings</MenuItem>
              <MenuItem onClick={() => goTo('/settings')}>Settings</MenuItem>
              {availableViewAsRoles.length > 1 && [
                <Divider key="view-as-divider" />,
                <Typography
                  key="view-as-label"
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', px: 2, pt: 1, pb: 0.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
                >
                  View as
                </Typography>,
                ...availableViewAsRoles.map((role) => {
                  // "Your role" = highest real role, not any held role. With
                  // root holding all three, without this we'd mark every
                  // item as "your role".
                  const isPrimary = role === primaryRole;
                  const active = role === effectiveRole;
                  return (
                    <MenuItem key={`view-as-${role}`} onClick={() => pickViewAs(role)}>
                      <ListItemIcon>
                        {active ? <CheckIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText primary={roleDisplayName(role)} secondary={isPrimary ? 'your role' : undefined} />
                    </MenuItem>
                  );
                }),
              ]}
              <Divider />
              <MenuItem
                onClick={async () => {
                  closeMenu();
                  await logout();
                  navigate('/login', { replace: true });
                }}
              >
                Sign out
              </MenuItem>
            </Menu>
          </Stack>
        </Toolbar>

        {/* WAF yellow hairline */}
        <Box sx={{ height: 3, backgroundColor: WAF_YELLOW }} />

        {/* Secondary nav — matches AppBar paper colour so light/dark stays consistent */}
        <Box
          sx={(t) => ({
            px: { xs: 1.5, sm: 4 },
            backgroundColor: t.palette.background.paper,
            display: 'flex',
            alignItems: 'stretch',
            // On phones the primary Tabs become a scrollable strip; allow the
            // "Admin tools" button to wrap onto its own line if it can't fit
            // alongside, so it always stays reachable. rowGap separates the
            // wrapped rows; the small bottom padding keeps the tab underline off
            // the content border below.
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            rowGap: { xs: 1, sm: 0 },
            pb: { xs: 0.75, sm: 0 },
          })}
        >
          <Tabs
            value={activeTab === -1 ? false : activeTab}
            // Scroll the primary tabs on phones instead of overflowing the row.
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile
            TabIndicatorProps={{ sx: { backgroundColor: WAF_YELLOW, height: 3 } }}
            sx={{
              minHeight: 44,
              // Take the available width on phones so the scroll strip has room
              // and the Admin button can wrap beneath it.
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              maxWidth: '100%',
              '& .MuiTab-root': {
                minHeight: 44,
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                transition: 'color 120ms ease',
              },
              '& .MuiTab-root:hover': { color: 'text.primary' },
              '& .Mui-selected': { color: 'text.primary !important' },
            }}
          >
            {visibleTabs.map((t) => (
              <Tab key={t.to} label={t.label} component={RouterLink} to={t.to} />
            ))}
          </Tabs>

          {adminTabs.length > 0 && (
            <>
              <Button
                onClick={(e) => setAdminAnchor(e.currentTarget)}
                startIcon={<AdminPanelSettingsOutlinedIcon />}
                endIcon={<KeyboardArrowDownIcon />}
                aria-haspopup="menu"
                aria-expanded={adminOpen}
                // Tinted + bordered in WAF yellow so the super-admin-only tools
                // read as a distinct section, visually separate from the shared
                // primary tabs.
                sx={{
                  alignSelf: 'center',
                  // No left margin once it wraps below the scroll strip on xs;
                  // a small bottom margin keeps it off the hairline-adjacent edge.
                  ml: { xs: 0, sm: 1.5 },
                  mb: { xs: 0.5, sm: 0 },
                  flexShrink: 0,
                  px: 1.5,
                  textTransform: 'none',
                  borderRadius: 1.5,
                  color: 'text.primary',
                  border: `1px solid ${WAF_YELLOW}`,
                  backgroundColor: onAdminPage ? 'rgba(255,204,0,0.20)' : 'rgba(255,204,0,0.08)',
                  '&:hover': { backgroundColor: 'rgba(255,204,0,0.24)', color: 'text.primary' },
                }}
              >
                Admin tools
              </Button>
              <Menu
                anchorEl={adminAnchor}
                open={adminOpen}
                onClose={() => setAdminAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
              >
                {visibleAdminGroups.flatMap((group, gi) => [
                  gi > 0 ? <Divider key={`admin-divider-${gi}`} component="li" /> : null,
                  <ListSubheader
                    key={`admin-heading-${gi}`}
                    disableSticky
                    sx={{
                      lineHeight: '32px',
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      backgroundColor: 'transparent',
                    }}
                  >
                    {group.heading}
                  </ListSubheader>,
                  ...group.tabs.map((t) => (
                    <MenuItem
                      key={t.to}
                      selected={t.match.some((p) => pathname.startsWith(p))}
                      onClick={() => {
                        setAdminAnchor(null);
                        navigate(t.to);
                      }}
                    >
                      {t.label}
                    </MenuItem>
                  )),
                ])}
              </Menu>
            </>
          )}
        </Box>
      </AppBar>

      {isSimulating && viewAsRole && (
        <Box
          sx={(t) => ({
            backgroundColor: t.palette.mode === 'dark' ? 'rgba(255, 204, 0, 0.12)' : 'rgba(255, 204, 0, 0.20)',
            borderBottom: `1px solid ${t.palette.divider}`,
            px: { xs: 1.5, sm: 4 },
            py: 1,
            display: 'flex',
            alignItems: 'center',
            // Let the "Exit view-as" button drop below the message on narrow
            // screens rather than squashing the text.
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: { xs: 1, sm: 2 },
          })}
        >
          <VisibilityOutlinedIcon fontSize="small" sx={{ color: 'text.primary' }} />
          <Typography variant="body2" sx={{ flex: { xs: '1 1 60%', sm: 1 }, fontWeight: 500 }}>
            Viewing as <strong>{roleDisplayName(viewAsRole)}</strong> for demo purposes
          </Typography>
          <Button
            size="small"
            onClick={() => {
              // Clear the simulated role and re-home via "/" — RoleHome
              // routes the user to the correct landing page for their
              // real role. Without the navigate, exiting on an
              // entrant-only route (e.g. /entrant) would strand a staff
              // user on a page that's no longer in their visible nav.
              setViewAsRole(null);
              navigate('/');
            }}
            sx={(t) => ({
              textTransform: 'none',
              color: 'text.primary',
              fontWeight: 600,
              '&:hover': { backgroundColor: t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
            })}
          >
            Exit view-as
          </Button>
        </Box>
      )}

      <Box component="main" sx={{ flex: 1, py: 4, px: { xs: 2, sm: 4 } }}>
        {/* Capped at 1200 for readability on most pages; wide-grid pages
            (Onboarding) opt into full width via isWideContent so their
            right-hand columns aren't clipped. */}
        <Box sx={{ maxWidth: isWideContent ? '100%' : isEntryDetail ? 'min(1600px, 72vw)' : 1200, mx: 'auto' }}>{children}</Box>
      </Box>
    </Box>
  );
}
